import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Input,
  Button,
  Heading,
  Text,
  Link,
  useToast,
  Checkbox,
  FormControl,
  FormLabel,
  Container,
  useColorModeValue,
  Divider,
  HStack,
  Select,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { PeerxLogo } from "../../components/icons/Icons";
import { fetchUser, fetchUserProfile } from "../../services/userService";
import { fetchKYCStatus } from "../../services/kycService";
import { useTranslation } from "react-i18next";

const MotionButton = motion(Button);

const SignUpPage = () => {
  const { t, i18n } = useTranslation();
  const { createUser, loginWithGoogle } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    dateOfBirth: "",
    agreedToTerms: false,
    referredByCode: "", // Added referred by code in initial state
  });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  // Theme-based colors
  const textColor = useColorModeValue("brand.400", "white");
  const bgColor = useColorModeValue("brand.300", "gray.900");
  const cardBgColor = useColorModeValue("brand.100", "gray.700");

  // **Prefill referred by code from localStorage**
  useEffect(() => {
    const storedReferral = localStorage.getItem("referredByCode") || "";
    if (storedReferral) {
      setFormData((prev) => ({ ...prev, referredByCode: storedReferral }));
    }
  }, []);

  // Handle language change
  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSignUp = async () => {
    const { email, password, confirmPassword, dateOfBirth, agreedToTerms, referredByCode } = formData;

    if (!email || !password || !confirmPassword || !dateOfBirth || !agreedToTerms) {
      toast({
        title: t("signuppage.missing_information_title"),
        description: t("signuppage.missing_information_message"),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: t("signuppage.password_mismatch_title"),
        description: t("signuppage.password_mismatch_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      await createUser(email, password, { dateOfBirth, agreedToTerms, referredByCode });
      toast({
        title: t("signuppage.account_created_title"),
        description: t("signuppage.account_created_message"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // **Clear referral code after successful registration**
      localStorage.removeItem("referredByCode");

      navigate("/auth/info-details");
    } catch (error) {
      toast({
        title: t("signuppage.sign_up_error_title"),
        description: error.message || t("signuppage.sign_up_error_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);

      const user = await loginWithGoogle();
      if (!user?.uid) {
        throw new Error(t("signuppage.google_signin_invalid_user"));
      }

      const userProfileData = await fetchUserProfile(user.uid);
      if (!userProfileData) {
        toast({
          title: t("signuppage.incomplete_profile_title"),
          description: t("signuppage.incomplete_profile_message"),
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        navigate("/auth/info-details");
        return;
      }

      const userData = await fetchUser(user.uid);

      if (!userData.emailVerified || !userData.phoneVerified) {
        navigate("/auth/verify");
        return;
      }

      const kycStatus = await fetchKYCStatus(user.uid);
      if (!kycStatus) {
        navigate("/personal/doc-info");
        return;
      }

      if (kycStatus !== "approved" && kycStatus !== "pending") {
        toast({
          title: t("signuppage.kyc_pending_title"),
          description: t("signuppage.kyc_pending_message"),
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        navigate("/personal/doc-info");
        return;
      }

      toast({
        title: t("signuppage.google_signin_success_title"),
        description: t("signuppage.google_signin_success_message"),
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      navigate("/personal/dashboard");
    } catch (error) {
      console.error("[GoogleSignIn] Error:", error);
      toast({
        title: t("signuppage.google_signin_error_title"),
        description: error.message || t("signuppage.google_signin_error_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box py={10} bg={bgColor} minH="100vh">
      {/* Language Switcher */}
      <Box position="fixed" top="4" right="4" zIndex="120" p={2}>
        <Select
          value={i18n.language}
          onChange={handleLanguageChange}
          width="120px"
          bg="white"
          color="black"
          size="sm"
        >
          <option value="en">English</option>
          <option value="fr">Français</option>
          <option value="es">Español</option>
        </Select>
      </Box>

      <Container maxW="md">
        {/* Logo */}
        <Heading mb="20px" size="2xl" fontFamily="Poppins" color="brand.200" fontWeight="bold" letterSpacing="wider" as={RouterLink} to="/">
          <PeerxLogo h="90px" w="160px" />
        </Heading>
        <Box
          as={motion.div}
          bg={cardBgColor}
          borderRadius="lg"
          shadow="lg"
          p={8}
          mt="20px"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <VStack spacing={6} align="stretch">
            <Box textAlign="center" mb={4}>
              <Heading size="lg" textStyle="mainTitle" color="brand.300">
                {t("signuppage.signup_title")}
              </Heading>
            </Box>

            <FormControl isRequired>
              <FormLabel color={textColor}>{t("signuppage.email_label")}</FormLabel>
              <Input
                name="email"
                type="email"
                bg="white"
                value={formData.email}
                onChange={handleChange}
                placeholder={t("signuppage.email_placeholder")}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={textColor}>{t("signuppage.password_label")}</FormLabel>
              <Input
                name="password"
                type="password"
                bg="white"
                value={formData.password}
                onChange={handleChange}
                placeholder={t("signuppage.password_placeholder")}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={textColor}>{t("signuppage.confirm_password_label")}</FormLabel>
              <Input
                name="confirmPassword"
                type="password"
                bg="white"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t("signuppage.confirm_password_placeholder")}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={textColor}>{t("signuppage.dob_label")}</FormLabel>
              <Input
                name="dateOfBirth"
                type="date"
                bg="white"
                value={formData.dateOfBirth}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={textColor}>{t("signuppage.referral_code_label")}</FormLabel>
              <Input
                name="referredByCode"
                type="text"
                bg="white"
                value={formData.referredByCode} // ✅ Prefilled from localStorage
                onChange={handleChange}
                placeholder="Enter Referral Code"
              />
            </FormControl>

            <FormControl isRequired>
              <Checkbox
                borderColor="brand.500"
                name="agreedToTerms"
                isChecked={formData.agreedToTerms}
                onChange={handleChange}
              >
                {t("signuppage.agree_to_terms")}
              </Checkbox>
            </FormControl>
          </VStack>
        </Box>

        <VStack spacing={4} mt={6}>
          <MotionButton
            variant="solid"
            w="full"
            onClick={handleSignUp}
            isLoading={loading}
            loadingText={t("signuppage.creating_account")}
            disabled={loading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t("signuppage.sign_up_button")}
          </MotionButton>

          <MotionButton
            variant="outline"
            w="full"
            onClick={handleGoogleSignIn}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t("signuppage.signup_with_google")}
          </MotionButton>
        </VStack>

        <Divider my={6} />

        <Text fontSize="sm" color="gray.500" textAlign="center">
          {t("signuppage.already_have_account")}{" "}
          <Link as={RouterLink} to="/auth/sign-in" color="brand.200" fontWeight="bold">
            {t("signuppage.login_link")}
          </Link>
        </Text>
      </Container>
    </Box>
  );
};

export default SignUpPage;
